import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Edit2 } from "lucide-react";

import { api } from "@/lib/api";
import { UseCaseEditorDrawer } from "@/pages/admin/UseCaseEditorDrawer";
import { HardwareClassEditorDrawer } from "@/pages/admin/HardwareClassEditorDrawer";

/**
 * WorkstationsAdminPanel — Workstations Final System Pass (Feb 2026)
 *
 * Custom admin panel (not part of the generic RESOURCES list) because
 * workstations are a two-layer product model:
 *
 *   • Use Cases (customer-facing products)      -> UseCaseEditorDrawer
 *   • Hardware Classes (internal pricing floor) -> HardwareClassEditorDrawer
 *
 * Reads:  GET /api/use-cases      (public — returns hydrated products)
 *         GET /api/software       (public — for the recommended-software
 *                                  dropdown options in the drawer)
 * Writes: PUT /api/admin/use-cases/{slug}
 *         PUT /api/admin/hardware-classes/{slug}
 *
 * We derive hardware_classes from the hydrated use_cases response
 * (each `resolved_hardware_class` is the same object per class), which
 * keeps the panel a single fetch.
 */
export const WorkstationsAdminPanel = () => {
  const [useCases, setUseCases] = useState([]);
  const [hardwareClasses, setHardwareClasses] = useState([]);
  const [softwareOptions, setSoftwareOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUseCase, setEditingUseCase] = useState(null);
  const [editingHc, setEditingHc] = useState(null);
  const [view, setView] = useState("use_cases"); // "use_cases" | "hardware_classes"

  const load = async () => {
    setLoading(true);
    try {
      const [ucRes, swRes] = await Promise.all([
        api.get("/use-cases"),
        api.get("/software").catch(() => ({ data: [] })),
      ]);
      const uc = Array.isArray(ucRes.data) ? ucRes.data : [];
      setUseCases(uc);
      // De-dupe hardware classes by slug.
      const hcMap = {};
      for (const item of uc) {
        const hc = item.resolved_hardware_class;
        if (hc?.slug) hcMap[hc.slug] = hc;
      }
      setHardwareClasses(Object.values(hcMap));
      setSoftwareOptions(
        (Array.isArray(swRes.data) ? swRes.data : [])
          .filter((s) => s?.slug && s?.status === "active")
          .map((s) => ({ slug: s.slug, name: s.name }))
      );
    } catch (e) {
      toast.error("Failed to load workstations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveUseCase = async (form) => {
    try {
      // The public GET returns extra derived fields (resolved_hardware_class);
      // strip those out so we don't try to write them back.
      const {
        resolved_hardware_class, id, ...writable
      } = form;
      await api.put(`/admin/use-cases/${form.slug}`, writable);
      toast.success(`Saved · ${form.display_name}`);
      setEditingUseCase(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  const saveHardwareClass = async (form) => {
    try {
      const { id, ...writable } = form;
      await api.put(`/admin/hardware-classes/${form.slug}`, writable);
      toast.success(`Saved · ${form.display_name}`);
      setEditingHc(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl font-bold">Workstations</h2>
        <div className="flex gap-2" data-testid="workstations-admin-tabs">
          <button
            data-testid="workstations-admin-view-use-cases"
            onClick={() => setView("use_cases")}
            className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-widest transition-colors ${
              view === "use_cases"
                ? "bg-[#FF6600] text-white"
                : "bg-[#1A1A1A] border border-[#262626] text-white hover:border-[#FF6600]"
            }`}
          >
            Use Cases ({useCases.length})
          </button>
          <button
            data-testid="workstations-admin-view-hardware-classes"
            onClick={() => setView("hardware_classes")}
            className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-widest transition-colors ${
              view === "hardware_classes"
                ? "bg-[#FF6600] text-white"
                : "bg-[#1A1A1A] border border-[#262626] text-white hover:border-[#FF6600]"
            }`}
          >
            Hardware Classes ({hardwareClasses.length})
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-[#A3A3A3] text-sm">Loading…</div>
      ) : view === "use_cases" ? (
        <div className="grid gap-3" data-testid="workstations-admin-list-use-cases">
          {useCases.map((uc) => (
            <div
              key={uc.id}
              data-testid={`workstations-admin-uc-${uc.slug}`}
              className="ur-card p-4 flex items-center gap-4"
            >
              {uc.image && (
                <img src={uc.image} alt="" className="w-16 h-12 rounded-lg object-cover bg-[#0F0F0F]" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-semibold">{uc.display_name}</div>
                  {!uc.active && (
                    <span className="text-[10px] uppercase tracking-widest text-[#F59E0B] border border-[#F59E0B]/40 rounded-full px-2 py-0.5">
                      Hidden
                    </span>
                  )}
                </div>
                <div className="text-[12px] text-[#A3A3A3] mt-0.5 truncate">
                  {uc.byline || uc.short_description || uc.slug}
                </div>
                <div className="text-[11px] text-[#7A7A7A] mt-1">
                  Hardware class: <span className="capitalize">{uc.default_hardware_class}</span>
                  {" · "}
                  Windows {uc.windows_supported ? "✓" : "—"}, Mac {uc.mac_supported ? "✓" : "—"}
                </div>
              </div>
              <button
                onClick={() => setEditingUseCase(uc)}
                data-testid={`workstations-admin-edit-uc-${uc.slug}`}
                className="ur-btn-secondary text-sm py-2 px-4"
              >
                <Edit2 className="w-3.5 h-3.5" /> Edit
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid gap-3" data-testid="workstations-admin-list-hardware-classes">
          {hardwareClasses.map((hc) => (
            <div
              key={hc.slug}
              data-testid={`workstations-admin-hc-${hc.slug}`}
              className="ur-card p-4 flex items-center gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-semibold capitalize">{hc.display_name || hc.slug}</div>
                  {hc.windows?.is_placeholder && (
                    <span className="text-[10px] uppercase tracking-widest text-[#F59E0B] border border-[#F59E0B]/40 rounded-full px-2 py-0.5">
                      Placeholder Pricing
                    </span>
                  )}
                </div>
                <div className="text-[12px] text-[#A3A3A3] mt-0.5 truncate">
                  {hc.description || "—"}
                </div>
                <div className="text-[11px] text-[#7A7A7A] mt-1">
                  Windows {hc.windows?.active ? `AWG ${hc.windows.price}` : "off"}
                  {" · "}
                  Mac {hc.mac?.active ? `AWG ${hc.mac.price}` : "off"}
                  {" · "}
                  {hc.addons?.length || 0} add-ons · {hc.min_spec?.length || 0} spec lines
                </div>
              </div>
              <button
                onClick={() => setEditingHc(hc)}
                data-testid={`workstations-admin-edit-hc-${hc.slug}`}
                className="ur-btn-secondary text-sm py-2 px-4"
              >
                <Edit2 className="w-3.5 h-3.5" /> Edit
              </button>
            </div>
          ))}
        </div>
      )}

      {editingUseCase && (
        <UseCaseEditorDrawer
          item={editingUseCase}
          softwareOptions={softwareOptions}
          onClose={() => setEditingUseCase(null)}
          onSave={saveUseCase}
        />
      )}
      {editingHc && (
        <HardwareClassEditorDrawer
          item={editingHc}
          onClose={() => setEditingHc(null)}
          onSave={saveHardwareClass}
        />
      )}
    </div>
  );
};
